/**
 * Telegram Web App API внутри Mini App — по практикам Sapero (docs/context/miniapp-practices-from-sapero.md):
 * скрипт telegram-web-app.js подключается только внутри Mini App; при открытии `ready()` + `expand()`; свайп вниз
 * выключен всё время (на Android свайп по полю сворачивал Mini App и убивал WebView посреди боя); подтверждение
 * закрытия — только в бою; полный экран — один раз за сессию, только iOS/Android и Bot API 8.0+; отступы выреза и кнопок
 * Telegram — в CSS `--app-inset-*` (safeArea + contentSafeArea) с кешем в sessionStorage. BackButton не ставим — он
 * прячет «Закрыть».
 *
 * Все вызовы терпят старый клиент: метода нет или он бросает `WebAppMethodUnsupported` — тихо ничего не делаем.
 */

export const TELEGRAM_WEBAPP_SRC = "https://telegram.org/js/telegram-web-app.js";

export interface Insets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/** Та часть `window.Telegram.WebApp`, которой пользуемся. Методы необязательны — у старого клиента их может не быть. */
export interface TelegramWebApp {
  version: string;
  platform: string;
  initData?: string;
  initDataUnsafe?: { user?: { language_code?: string; is_premium?: boolean }; start_param?: string };
  ready?: () => void;
  expand?: () => void;
  disableVerticalSwipes?: () => void;
  enableClosingConfirmation?: () => void;
  disableClosingConfirmation?: () => void;
  isFullscreen?: boolean;
  isActive?: boolean;
  requestFullscreen?: () => void;
  /** Bot API 8.0: выйти из полного экрана. */
  exitFullscreen?: () => void;
  isVersionAtLeast?: (version: string) => boolean;
  safeAreaInset?: Insets;
  contentSafeAreaInset?: Insets;
  onEvent?: (event: string, callback: (params?: unknown) => void) => void;
  offEvent?: (event: string, callback: (params?: unknown) => void) => void;
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
  setBottomBarColor?: (color: string) => void;
  /** Bot API 8.0: отправить подготовленное ботом сообщение (`savePreparedInlineMessage`). */
  openTelegramLink?(url: string): void;
  shareMessage?: (preparedMessageId: string, callback?: (sent: boolean) => void) => void;
  /** Bot API 7.8: сторис с картинкой по https-ссылке. */
  shareToStory?: (mediaUrl: string, params?: { text?: string }) => void;
  /** Bot API 6.1: открыть счёт по ссылке `createInvoiceLink`. */
  openInvoice?: (url: string, callback?: (status: InvoiceStatus) => void) => void;
  /** Bot API 6.9: системное окно «разрешить боту писать». */
  requestWriteAccess?: (callback?: (allowed: boolean) => void) => void;
  /** Bot API 8.0: системное окно «добавить на главный экран». */
  addToHomeScreen?: () => void;
  /** Bot API 8.0: есть ли уже ярлык. */
  checkHomeScreenStatus?: (callback?: (status: HomeScreenStatus) => void) => void;
}

/** Ярлык на главном экране: `missed` — можно предложить, `unknown` — телефон не сказал, `unsupported` — не умеет. */
export type HomeScreenStatus = "unsupported" | "unknown" | "added" | "missed";

/** Чем кончился счёт. */
export type InvoiceStatus = "paid" | "cancelled" | "failed" | "pending";

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

/** Окно, в которое грузим скрипт (в тестах — подставное). */
export interface ScriptHost {
  Telegram?: { WebApp?: TelegramWebApp };
  document: { createElement: (tag: "script") => HTMLScriptElement; head: { appendChild: (node: Node) => Node } };
}

const loading = new WeakMap<object, Promise<TelegramWebApp | null>>();

/**
 * Подключает telegram-web-app.js один раз на окно и отдаёт WebApp; `null` — скрипт не загрузился (следующий вызов
 * попробует снова). Если WebApp уже есть (подставной в e2e или скрипт уже стоит) — ничего не вставляет.
 */
export function loadTelegramWebApp(win: ScriptHost = window): Promise<TelegramWebApp | null> {
  const ready = win.Telegram?.WebApp;
  if (ready) return Promise.resolve(ready);
  let p = loading.get(win);
  if (p) return p;
  p = new Promise<TelegramWebApp | null>((resolve) => {
    try {
      const s = win.document.createElement("script");
      s.src = TELEGRAM_WEBAPP_SRC;
      s.async = true;
      s.onload = () => resolve(win.Telegram?.WebApp ?? null);
      s.onerror = () => {
        loading.delete(win);
        resolve(null);
      };
      win.document.head.appendChild(s);
    } catch {
      loading.delete(win);
      resolve(null);
    }
  });
  loading.set(win, p);
  return p;
}

/** Редактор сторис: метод есть у всех, работает с 7.8. */
export function canShareToStory(tg: TelegramWebApp): boolean {
  return Boolean(tg.shareToStory) && atLeast(tg, "7.8");
}

function atLeast(tg: TelegramWebApp, v: string): boolean {
  try {
    return tg.isVersionAtLeast ? tg.isVersionAtLeast(v) : false;
  } catch {
    return false;
  }
}

function safely(fn: () => void): void {
  try {
    fn();
  } catch {
    // WebAppMethodUnsupported и подобное — Mini App остаётся как был
  }
}

/** При открытии: страница готова, развернуть на всю высоту. */
export function expandMiniApp(tg: TelegramWebApp): void {
  safely(() => tg.ready?.());
  safely(() => tg.expand?.());
}

/** Свайп вниз не сворачивает Mini App (Bot API 7.7+) — на всё время Mini App. */
export function disableSwipes(tg: TelegramWebApp): void {
  if (atLeast(tg, "7.7")) safely(() => tg.disableVerticalSwipes?.());
}

/** Подтверждение закрытия: включено, пока идёт бой; вне боя — закрывается сразу (Bot API 6.2+). */
export function applyClosingConfirmation(tg: TelegramWebApp, inBattle: boolean): void {
  if (!atLeast(tg, "6.2")) return;
  safely(() => (inBattle ? tg.enableClosingConfirmation?.() : tg.disableClosingConfirmation?.()));
}

/** Флаг «полный экран уже просили» на сессию Mini App: вышел человек сам — обратно не загоняем. */
export const FULLSCREEN_ASKED_KEY = "nfg:tgFullscreenAsked";

/** Просить ли полный экран: только телефон (ios, android, android_x), Bot API 8.0+, и ещё не на весь экран. */
export function wantsFullscreen(tg: TelegramWebApp): boolean {
  if (!isPhone(tg) || tg.isFullscreen || !tg.requestFullscreen) return false;
  return atLeast(tg, "8.0");
}

/** Телефон: туда полный экран просим сами, кнопки нет. */
function isPhone(tg: TelegramWebApp): boolean {
  return /^(ios|android)/.test(tg.platform);
}

/**
 * Кнопка «На весь экран» — Telegram на компьютере и в вебе (macos, tdesktop, weba, webk…): Bot API 8.0+ и оба метода.
 * На телефоне и у клиента, не назвавшего платформу (`unknown`), кнопки нет.
 */
export function canToggleFullscreen(tg: TelegramWebApp): boolean {
  if (isPhone(tg) || !tg.platform || tg.platform === "unknown") return false;
  if (!tg.requestFullscreen || !tg.exitFullscreen) return false;
  return atLeast(tg, "8.0");
}

/** Полный экран один раз за сессию Mini App; `true` — попросили. */
export function requestFullscreenOnce(
  tg: TelegramWebApp,
  storage: Pick<Storage, "getItem" | "setItem"> | null,
): boolean {
  try {
    if (storage?.getItem(FULLSCREEN_ASKED_KEY)) return false;
    if (!wantsFullscreen(tg)) return false;
    storage?.setItem(FULLSCREEN_ASKED_KEY, "1");
    tg.requestFullscreen!();
    return true;
  } catch {
    return false;
  }
}

/** Системное окно «разрешить боту писать». `null` — старый клиент или метод бросил: ничего не узнали. */
export function requestWriteAccess(tg: TelegramWebApp): Promise<boolean | null> {
  if (!tg.requestWriteAccess || !atLeast(tg, "6.9")) return Promise.resolve(null);
  return new Promise((resolve) => {
    try {
      tg.requestWriteAccess!((allowed) => resolve(Boolean(allowed)));
    } catch {
      resolve(null);
    }
  });
}

/** Статус ярлыка; `null` — клиент до 8.0 или метод бросил. */
export function homeScreenStatus(tg: TelegramWebApp): Promise<HomeScreenStatus | null> {
  if (!tg.checkHomeScreenStatus || !atLeast(tg, "8.0")) return Promise.resolve(null);
  return new Promise((resolve) => {
    try {
      tg.checkHomeScreenStatus!((status) => resolve(status));
    } catch {
      resolve(null);
    }
  });
}

const ZERO: Insets = { top: 0, bottom: 0, left: 0, right: 0 };

/**
 * Сколько места занимают вырез, системные панели и кнопки Telegram. Не на весь экран — 0 (Telegram сам держит шапку).
 * На весь экран, но размеры ещё не пришли (все нули) — `null`: оставляем прежние (кеш сессии), иначе интерфейс прыгнет.
 */
export function appInsets(tg: TelegramWebApp): Insets | null {
  if (!tg.isFullscreen) return ZERO;
  const a = tg.safeAreaInset ?? ZERO;
  const b = tg.contentSafeAreaInset ?? ZERO;
  const r = { top: a.top + b.top, bottom: a.bottom + b.bottom, left: a.left + b.left, right: a.right + b.right };
  return r.top + r.bottom + r.left + r.right > 0 ? r : null;
}

/** Цвета Telegram вокруг страницы = фон игры. Только `#RRGGBB` и Bot API 6.9+ (нижняя панель — 7.10+). */
export function applyThemeColors(tg: TelegramWebApp, raw: string): void {
  const color = /^#[0-9a-f]{3}$/i.test(raw) ? `#${[...raw.slice(1)].map((c) => c + c).join("")}` : raw;
  if (!/^#[0-9a-f]{6}$/i.test(color) || !atLeast(tg, "6.9")) return;
  safely(() => tg.setHeaderColor?.(color));
  safely(() => tg.setBackgroundColor?.(color));
  if (atLeast(tg, "7.10")) safely(() => tg.setBottomBarColor?.(color));
}
